import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet } from '../../../services/apiClient';
import { getAdminEnquiry, listAdminEnquiries, updateAdminEnquiry } from '../../../api/cabsAdmin';

const tabs = [
  { key: 'current', label: 'Current' },
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'past', label: 'Past' },
  { key: 'pending', label: 'Pending' },
];

const statusOptions = [
  'NEW',
  'PENDING_CUSTOMER',
  'PENDING_INTERNAL',
  'CONFIRMED',
  'CANCELLED',
  'COMPLETED',
  'SPAM',
];

const stageOptions = ['LEAD', 'QUOTE_SENT', 'FOLLOW_UP', 'BOOKED', 'CLOSED'];

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : '-');
const formatDateTime = (value) => (value ? new Date(value).toLocaleString() : '-');
const formatMoney = (value) => (value || value === 0 ? `INR ${Number(value).toLocaleString()}` : '-');

const exportRowsToCsv = (rows) => {
  if (!rows.length) return;
  const columns = [
    'Date',
    'Booking Type',
    'Package/Route',
    'Customer',
    'Email',
    'Phone',
    'Travel Start',
    'Travel End',
    'Passengers',
    'Status',
    'Stage',
    'Source',
    'Quoted Amount',
  ];
  const escape = (value) => `"${String(value ?? '').replaceAll('"', '""')}"`;
  const data = rows.map((row) => [
    formatDate(row.enquiryDate || row.createdAt),
    row.enquiryType === 'PACKAGE' ? 'Package' : row.tripType || 'Cab trip',
    row.transportPackage?.title || `${row.pickupLocation?.name || '-'} -> ${row.dropLocation?.name || '-'}`,
    row.customerName || row.fullName || '',
    row.email || '',
    row.phone || '',
    formatDate(row.tripStartDate || row.pickupDate),
    formatDate(row.tripEndDate || row.returnDate),
    row.passengers || row.pax || '',
    row.status || '',
    row.stage || '',
    row.source || '',
    row.estimatedTotal || '',
  ]);
  const csv = [columns.join(','), ...data.map((row) => row.map(escape).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `cab-enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

const AdminCabsEnquiries = () => {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('current');
  const [status, setStatus] = useState('');
  const [stage, setStage] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [search, setSearch] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState('');
  const [detailOpen, setDetailOpen] = useState(false);
  const [notesInternal, setNotesInternal] = useState('');
  const [nextFollowUpAt, setNextFollowUpAt] = useState('');
  const [quotedAmount, setQuotedAmount] = useState('');

  const adminProfileQuery = useQuery({
    queryKey: ['admin', 'auth-me'],
    queryFn: () => apiGet('/api/admin/auth/me'),
  });

  const enquiryQuery = useQuery({
    queryKey: ['cabs', 'enquiries', tab, status, stage, fromDate, toDate, search, assignedTo, page],
    queryFn: () =>
      listAdminEnquiries({
        tab,
        status,
        stage,
        fromDate,
        toDate,
        q: search,
        assignedToAdminId: assignedTo || undefined,
        page,
        limit: 20,
      }),
  });

  const enquiries = enquiryQuery.data?.data || [];
  const pagination = enquiryQuery.data?.pagination || { page: 1, totalPages: 1, total: 0 };
  const counts = enquiryQuery.data?.counts || {};

  const detailQuery = useQuery({
    queryKey: ['cabs', 'enquiry-detail', selectedId],
    queryFn: () => getAdminEnquiry(selectedId),
    enabled: Boolean(selectedId && detailOpen),
  });

  const detail = detailQuery.data?.data || detailQuery.data || null;

  useEffect(() => {
    setNotesInternal(detail?.notes || '');
    setNextFollowUpAt(detail?.nextFollowUpAt ? new Date(detail.nextFollowUpAt).toISOString().slice(0, 16) : '');
    setQuotedAmount(detail?.estimatedTotal || '');
  }, [detail]);

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }) => updateAdminEnquiry(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cabs', 'enquiries'] });
      queryClient.invalidateQueries({ queryKey: ['cabs', 'enquiry-detail', selectedId] });
    },
  });

  const assignedOptions = useMemo(() => {
    const me = adminProfileQuery.data?.admin || adminProfileQuery.data;
    if (!me?.id) return [];
    return [{ id: me.id, label: `${me.name || me.email || 'Me'} (You)` }];
  }, [adminProfileQuery.data]);

  const quickStatusActions = [
    { label: 'Mark Confirmed', status: 'CONFIRMED' },
    { label: 'Mark Completed', status: 'COMPLETED' },
    { label: 'Mark Cancelled', status: 'CANCELLED' },
    { label: 'Mark Spam', status: 'SPAM' },
  ];

  const updateField = (id, payload) => {
    updateMutation.mutate({ id, payload });
  };

  const openDetail = (id) => {
    setSelectedId(id);
    setDetailOpen(true);
  };

  const saveDetail = () => {
    if (!selectedId) return;
    updateMutation.mutate({
      id: selectedId,
      payload: {
        notesInternal,
        nextFollowUpAt: nextFollowUpAt ? new Date(nextFollowUpAt).toISOString() : null,
        estimatedTotal: quotedAmount === '' ? null : Number(quotedAmount),
      },
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-emerald-600">Cab Services</p>
        <h1 className="text-2xl font-semibold text-slate-900">Booking & Enquiries</h1>
        <p className="text-sm text-slate-600">Track cab and transport package leads, follow-ups, and quoted amounts.</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {tabs.map((item) => (
          <button
            key={item.key}
            className={`btn btn-sm ${tab === item.key ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => {
              setTab(item.key);
              setPage(1);
            }}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-3">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
          <div className="text-xs text-slate-500">
            {pagination.total} enquiries
          </div>
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => exportRowsToCsv(enquiries)}
            disabled={!enquiries.length}
          >
            Export CSV
          </button>
        </div>

        <div className="row g-3">
          <div className="col-md-2">
            <label className="form-label text-xs">Status</label>
            <select className="form-select" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All</option>
              {statusOptions.map((option) => (
                <option key={option} value={option}>
                  {option.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-2">
            <label className="form-label text-xs">Stage</label>
            <select className="form-select" value={stage} onChange={(e) => setStage(e.target.value)}>
              <option value="">All</option>
              {stageOptions.map((option) => (
                <option key={option} value={option}>
                  {option.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-2">
            <label className="form-label text-xs">From</label>
            <input className="form-control" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </div>
          <div className="col-md-2">
            <label className="form-label text-xs">To</label>
            <input className="form-control" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </div>
          <div className="col-md-2">
            <label className="form-label text-xs">Assigned</label>
            <select className="form-select" value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)}>
              <option value="">All</option>
              {assignedOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-2">
            <label className="form-label text-xs">Search</label>
            <input
              className="form-control"
              placeholder="Name, email, phone"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="d-flex flex-wrap gap-3 text-xs text-slate-500">
          {Object.entries(counts.status || {}).map(([key, value]) => (
            <span key={key}>
              {key.replaceAll('_', ' ')}: <strong>{value}</strong>
            </span>
          ))}
        </div>

        <div className="table-responsive border rounded-lg">
          <table className="table table-sm mb-0 align-middle">
            <thead className="table-light">
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Package / Route</th>
                <th>Customer</th>
                <th>Contact</th>
                <th>Travel</th>
                <th>Pax</th>
                <th>Status</th>
                <th>Stage</th>
                <th>Source</th>
                <th>Quoted</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {enquiryQuery.isLoading && (
                <tr>
                  <td colSpan={12} className="text-center text-muted">
                    Loading enquiries...
                  </td>
                </tr>
              )}
              {!enquiryQuery.isLoading && enquiries.length === 0 && (
                <tr>
                  <td colSpan={12} className="text-center text-muted">
                    No enquiries yet.
                  </td>
                </tr>
              )}
              {enquiries.map((enquiry) => (
                <tr key={enquiry.id}>
                  <td>{formatDate(enquiry.enquiryDate || enquiry.createdAt)}</td>
                  <td>{enquiry.enquiryType === 'PACKAGE' ? 'Package' : enquiry.tripType || 'Cab trip'}</td>
                  <td>
                    {enquiry.transportPackage?.title ||
                      `${enquiry.pickupLocation?.name || '-'} -> ${enquiry.dropLocation?.name || '-'}`}
                  </td>
                  <td>
                    <div className="fw-semibold">{enquiry.customerName || enquiry.fullName || '-'}</div>
                    <div className="small text-muted">
                      {enquiry.transportPackageVariant?.vehicleCategory?.name || ''}
                    </div>
                  </td>
                  <td>
                    <div>{enquiry.phone || '-'}</div>
                    <div className="small text-muted">{enquiry.email || '-'}</div>
                  </td>
                  <td>
                    {formatDate(enquiry.tripStartDate || enquiry.pickupDate)} / {formatDate(enquiry.tripEndDate || enquiry.returnDate)}
                  </td>
                  <td>{enquiry.passengers || enquiry.pax || '-'}</td>
                  <td>
                    <select
                      className="form-select form-select-sm"
                      value={enquiry.status || ''}
                      onChange={(e) => updateField(enquiry.id, { status: e.target.value })}
                    >
                      {statusOptions.map((option) => (
                        <option key={option} value={option}>
                          {option.replaceAll('_', ' ')}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>{enquiry.stage?.replaceAll('_', ' ') || '-'}</td>
                  <td>{enquiry.source || '-'}</td>
                  <td>{formatMoney(enquiry.estimatedTotal)}</td>
                  <td className="text-end">
                    <button className="btn btn-sm btn-outline-primary" onClick={() => openDetail(enquiry.id)}>
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="d-flex flex-wrap justify-content-between align-items-center text-sm text-slate-600">
          <span>
            Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
          </span>
          <div className="btn-group">
            <button
              className="btn btn-outline-secondary btn-sm"
              disabled={pagination.page <= 1}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
            >
              Prev
            </button>
            <button
              className="btn btn-outline-secondary btn-sm"
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => setPage((current) => current + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <Modal show={detailOpen} onHide={() => setDetailOpen(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Enquiry details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailQuery.isLoading && <div className="text-center text-muted">Loading details...</div>}
          {!detailQuery.isLoading && detail && (
            <div className="row g-3">
              <div className="col-md-6">
                <h6 className="text-xs uppercase text-slate-400">Customer</h6>
                <p className="mb-1">{detail.customerName || detail.fullName || '-'}</p>
                <p className="mb-1">{detail.phone || '-'}</p>
                <p className="mb-0">{detail.email || '-'}</p>
              </div>
              <div className="col-md-6">
                <h6 className="text-xs uppercase text-slate-400">Trip summary</h6>
                <p className="mb-1">
                  {detail.enquiryType === 'PACKAGE'
                    ? detail.transportPackage?.title
                    : `${detail.pickupLocation?.name || '-'} -> ${detail.dropLocation?.name || '-'}`}
                </p>
                <p className="mb-1">Trip type: {detail.tripType || detail.enquiryType}</p>
                <p className="mb-0">
                  Dates: {formatDate(detail.tripStartDate || detail.pickupDate)} - {formatDate(detail.tripEndDate || detail.returnDate)}
                </p>
              </div>
              <div className="col-md-6">
                <label className="form-label text-xs">Stage</label>
                <select
                  className="form-select"
                  value={detail.stage || ''}
                  onChange={(e) => updateField(selectedId, { stage: e.target.value })}
                >
                  {stageOptions.map((option) => (
                    <option key={option} value={option}>
                      {option.replaceAll('_', ' ')}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label text-xs">Assign to</label>
                <select
                  className="form-select"
                  value={detail.assignedToAdminId || ''}
                  onChange={(e) => updateField(selectedId, { assignedToAdminId: e.target.value || null })}
                >
                  <option value="">Unassigned</option>
                  {assignedOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label text-xs">Next follow-up</label>
                <input
                  type="datetime-local"
                  className="form-control"
                  value={nextFollowUpAt}
                  onChange={(e) => setNextFollowUpAt(e.target.value)}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label text-xs">Quoted amount</label>
                <input
                  type="number"
                  className="form-control"
                  value={quotedAmount}
                  onChange={(e) => setQuotedAmount(e.target.value)}
                  placeholder="INR"
                />
              </div>
              <div className="col-12">
                <h6 className="text-xs uppercase text-slate-400">Admin notes</h6>
                <textarea
                  className="form-control"
                  rows={4}
                  value={notesInternal}
                  onChange={(e) => setNotesInternal(e.target.value)}
                />
              </div>
              <div className="col-12">
                <div className="text-xs text-slate-500">
                  Source: {detail.source || '-'} | Last updated: {formatDateTime(detail.updatedAt)}
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <div className="btn-group">
            {quickStatusActions.map((action) => (
              <button
                key={action.status}
                className="btn btn-outline-secondary"
                onClick={() => updateField(selectedId, { status: action.status })}
              >
                {action.label}
              </button>
            ))}
          </div>
          <div className="d-flex gap-2">
            <button className="btn btn-secondary" onClick={() => setDetailOpen(false)}>
              Close
            </button>
            <button className="btn btn-primary" onClick={saveDetail} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? 'Saving...' : 'Save changes'}
            </button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminCabsEnquiries;
